import { getUrlWithVariant } from "@shopify/theme-product-form";
import isMobile from "is-mobile";
import scrollContainer from "@/lib/scroll-container";
import getMediaQuery from "@/lib/media-queries";
import debounce from "@/utils/debounce";

import { add, listen, qs, qsa, remove, toggle } from "@fluorescent/dom";
import { cart } from "@/glow";
import { emit } from "@/glow/events";

import ProductForm from "@/lib/product-form";
import productBlocks from "@/lib/product/product-blocks";
import productLightbox from "@/lib/product-lightbox";
import { switchImage } from "@/utils";
import {
  sidebarRecommendations,
  media,
  giftCardRecipient,
  stickyAtcBar,
  updateInventoryCounterFromHTML,
} from "@/lib/product";
import { updateRecentProducts } from "@/lib/recently-viewed-product-manager";
import StickyScroll from "@/lib/sticky-scroll";
import storeAvailability from "@/lib/store-availability";
import dispatchCustomEvent from "@/lib/dispatch-custom-event";
import srraf from "srraf";

const classes = {
  hidden: "hidden",
};

const selectors = {
  form: "[data-product-form]",
  variantIdInput: "[data-variant-id-input]",
  currentVariantJson: "[data-current-variant-json]",
  addToCart: "[data-add-to-cart]",
  thumbs: "[data-product-thumbnails]",
  thumb: "[data-product-thumbnail]",
  thumbMobile: "[data-thumbnail-size='mobile'] [data-product-thumbnail]",
  mobileThumbById: id =>
    `[data-thumbnail-size='mobile'] [data-thumbnail-id='${id}']`,
  storeAvailability: "[data-store-availability-container]",
  quantityError: "[data-quantity-error]",
  photosMobile: ".product__media-container.below-mobile",
  photosDesktop: ".product__media-container.above-mobile",
  photosDesktopLargeImageContainer:
    ".product__media-container.above-mobile .product__media",
  productMedia: ".product__media",
  photosQuickDesktop: ".quick-product .quick-product__left",
  photosQuickMobile:
    ".quick-product .product__block--product-header-image-wrap",
  quickCart: ".quick-cart",
  purchaseConfirmation: ".purchase-confirmation-popup",
  giftCardRecipientContainer: ".product-form__gift-card-recipient",
  externalVideoOverlay: ".external-video-overlay",
};

class Product {
  constructor(node) {
    this.container = node;

    const {
      isFullProduct,
      isFeaturedProduct,
      enableStickyContainer,
      scrollerId,
      sectionId,
      enableMultipleVariantMedia,
      initialMediaId,
      productHandle,
    } = this.container.dataset;

    this.isQuickProduct = false;
    this.isFullProduct = isFullProduct;
    this.isFeaturedProduct = isFeaturedProduct;
    this.scrollerId = scrollerId;
    this.sectionId = sectionId;
    this.enableMultipleVariantMedia = enableMultipleVariantMedia == "true";
    this.previousMediaId = initialMediaId;

    if (this.container.hasAttribute("data-quick-product")) {
      this.isQuickProduct = true;
      this.quickProductType = this.container.dataset.quickProductType;
    }

    this.productHandle = productHandle;
    this.formElement = qs(selectors.form, this.container);
    this.quantityError = qs(selectors.quantityError, this.container);

    this.viewInYourSpace = qs("[data-in-your-space]", this.container);
    this.viewInYourSpaceWrap = qs(
      ".product__view-in-space-wrap",
      this.container
    );

    this.viewInYourSpace &&
      toggle(this.viewInYourSpaceWrap, "visible", isMobile());

    // Handle Surface pickup
    this.availability = null;
    this.storeAvailabilityContainer = qs(
      selectors.storeAvailability,
      this.container
    );

    let initialVariant = null;

    if (this.formElement) {
      initialVariant = this._getCurrentVariant();

      this.productBlocks = productBlocks(this.container);

      if (this.storeAvailabilityContainer && initialVariant) {
        this.availability = storeAvailability(
          this.storeAvailabilityContainer,
          { title: this.storeAvailabilityContainer.dataset.productTitle },
          initialVariant
        );
      }

      this.productForm = ProductForm(this.container, this.formElement, {
        onOptionChange: event => this.onOptionChange(event),
        onFormSubmit: event => this.onFormSubmit(event),
        onQuantityChange: event => this.onQuantityChange(event),
      });
    } else {
      this.productBlocks = productBlocks(this.container);
    }

    this.sidebarRecommendations = sidebarRecommendations(this.container);

    if (enableStickyContainer === "true" && !isMobile()) {
      this.stickyScroll = StickyScroll(this.container);
    }

    if (this.isFullProduct && this.productHandle) {
      updateRecentProducts(this.productHandle);
    }

    this._loadMedia();

    // Must run after _loadMedia() — it depends on this.productThumbnails.
    if (initialVariant) this.scrollThumbnails(initialVariant);

    // Gift card recipient
    this.giftCardRecipient = giftCardRecipient(this.container);

    // Sticky ATC Bar
    this.stickyAtcBar = stickyAtcBar(this.container);
  }

  // initialize media
  _loadMedia() {
    this.photosDesktop = qs(selectors.photosDesktop, this.container);
    this.photosDesktopLargeImageContainer = qs(
      selectors.photosDesktopLargeImageContainer,
      this.container
    );
    this.photosQuickDesktop = qs(selectors.photosQuickDesktop, this.container);
    this.photosQuickMobile = qs(selectors.photosQuickMobile, this.container);

    this.productMedia = media(this.container);
    this.externalVideoOverlayEls = qsa(
      selectors.externalVideoOverlay,
      this.container
    );

    productLightbox();
    this._initThumbnails();
    this._initEvents();
    this.setupMobileSlider();
  }

  // initialize thumbnails
  _initThumbnails() {
    this.productThumbnails = qsa(selectors.thumbs, this.container);
    this.productThumbnailItems = qsa(selectors.thumb, this.container);
    this.productThumbnailItemsMobile = qsa(
      selectors.thumbMobile,
      this.container
    );

    this.productThumbnailsScrollers = this.productThumbnails.map(set => {
      return scrollContainer.initScroller(set, this.isQuickProduct);
    });

    if (
      this.photosDesktop?.getAttribute("data-thumbnails-position") == "left"
    ) {
      if (!qs(".placeholder-image", this.photosDesktop)) {
        // We want the thumbnails container height to match the first image's
        // height. This seems overly complex in order to achieve this when
        // image shape is set to "natural"
        const firstVisibleImage =
          this.photosDesktopLargeImageContainer.querySelector(
            ".product__media-item:not(.hidden) img"
          );

        this.firstVisibleImageAspectRatio =
          firstVisibleImage &&
          firstVisibleImage.offsetWidth / firstVisibleImage.offsetHeight;

        this.setThumbnailsHeight = () => {
          if (this.firstVisibleImageAspectRatio) {
            this.photosDesktop.style.setProperty(
              "--first-large-image-height",
              `${
                this.photosDesktopLargeImageContainer.offsetWidth /
                this.firstVisibleImageAspectRatio
              }px`
            );
          }
        };

        this.widthWatcher = srraf(({ vw }) => {
          this.setThumbnailsHeight();
        });

        this.setThumbnailsHeight();
      }
    }
  }

  _initEvents() {
    this.events = [
      listen(this.productThumbnailItems, "click", e =>
        this._handleThumbnailClick(e)
      ),
      listen(this.productThumbnailItemsMobile, "click", e =>
        this._handleThumbnailClick(e)
      ),
      listen(this.externalVideoOverlayEls, "click", e =>
        this._handleVideoOverlayClick(e)
      ),
    ];
  }

  _handleThumbnailClick(e) {
    e.preventDefault();
    this.productMedia.pauseAllMedia();

    const {
      currentTarget: { dataset },
    } = e;

    // Immediately update the active state
    this.productThumbnailItems.forEach(thumb => remove(thumb, "active"));
    add(e.currentTarget, "active");

    if (this.isQuickProduct) {
      if (this.quickProductType == "view") {
        switchImage({
          container: this.photosQuickDesktop,
          imageId: dataset.thumbnailId,
          inYourSpaceButton: this.viewInYourSpace,
        });
        switchImage({
          container: this.photosQuickMobile,
          imageId: dataset.thumbnailId,
          inYourSpaceButton: this.viewInYourSpace,
        });
      }
    } else {
      switchImage({
        container: this.photosDesktop,
        imageId: dataset.thumbnailId,
        inYourSpaceButton: this.viewInYourSpace,
      });
      switchImage({
        container: this.photosMobile,
        imageId: dataset.thumbnailId,
        inYourSpaceButton: this.viewInYourSpace,
        hideImages: false,
      });

      dispatchCustomEvent(`scroll-slider-${this.scrollerId}:go-to-slide`, {
        slideIndex: dataset.thumbnailIndex,
      });
    }
  }

  _handleVideoOverlayClick(e) {
    e.preventDefault();
    e.srcElement.remove();
  }

  setupMobileSlider() {
    if (this.isQuickProduct) return;

    this.events.push(
      listen(
        document,
        `scroll-slider-${this.scrollerId}:slide-changed`,
        evt => {
          this.productMedia.pauseAllMedia();
          const activeSlide = evt.detail.currentElement;
          const activeSlideImage = qs(
            ".image, [data-media-type='video'], .shopify-model-viewer-ui, .js-youtube, .js-vimeo",
            activeSlide
          );
          const slideContainerMobile = qs(
            `${selectors.photosMobile} ${selectors.productMedia}`,
            this.container
          );

          if (this.viewInYourSpace) {
            if (activeSlide.dataset.mediaType === "model") {
              this.viewInYourSpace.setAttribute(
                "data-shopify-model3d-id",
                activeSlide.dataset.mediaItemId
              );
            }
          }

          // When we're in the editor and switch from desktop to mobile, the height gets calculated before the
          // content is properly in place and leaves a big gap below the image. This short timeout prevents that.
          if (Shopify.designMode) {
            setTimeout(() => {
              slideContainerMobile.style.height = `${activeSlideImage.clientHeight}px`;
            }, 60);
          } else {
            slideContainerMobile.style.height = `${activeSlideImage.clientHeight}px`;
          }

          if (this.productThumbnailItemsMobile.length) {
            debounce(() => {
              this.productThumbnailItemsMobile.forEach(thumb =>
                remove(thumb, "active")
              );

              const targetThumb = qs(
                selectors.mobileThumbById(activeSlide.dataset.mediaItemId),
                this.container
              );

              add(targetThumb, "active");
            }, 200)();
          }
        }
      )
    );
  }

  // Reads the liquid-rendered current variant from [data-current-variant-json].
  _getCurrentVariant(scope = this.container) {
    const el = qs(selectors.currentVariantJson, scope);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  // The Section Rendering API is the source of truth for resolving option
  // values to a variant; we swap the rendered HTML into each variant-dependent
  // block when the response returns.
  onOptionChange({ srcElement }) {
    // Option inputs live in the variant-picker block, not inside <form>.
    const optionInputs = qsa('[name^="options"]', this.container);
    const optionValueIds = [];

    optionInputs.forEach(input => {
      let id;

      if (input.tagName === "SELECT") {
        const selected = input.options[input.selectedIndex];
        id = selected?.dataset.optionValueId;
      } else if (input.checked) {
        id = input.dataset.optionValueId;
      }

      if (id) optionValueIds.push(id);
    });

    if (!optionValueIds.length) return;

    const productURL = `${window.theme.routes.products}/${this.productHandle}`;
    const params = new URLSearchParams({
      section_id: this.sectionId,
      option_values: optionValueIds.join(","),
    });

    if (this.isQuickProduct) {
      params.set("view", `quick-${this.quickProductType}`);
    }

    // Cancel any in-flight variant fetch so rapid option clicks don't race —
    // the latest selection always wins.
    this.variantFetchController?.abort();
    const controller = new AbortController();
    this.variantFetchController = controller;

    // Loading state: lock inputs immediately so clicks don't queue while we
    // resolve. `inert` blocks pointer, keyboard, and hides from AT. The visual
    // dim is deferred so fast networks don't flicker — only dim if the fetch
    // takes longer than the threshold.
    this.container.inert = true;
    this.container.setAttribute("data-variant-loading", "true");
    const dimTimer = setTimeout(() => {
      this.container.setAttribute("data-variant-dimmed", "true");
    }, 200);

    const cleanupLoading = () => {
      clearTimeout(dimTimer);
      this.container.inert = false;
      this.container.removeAttribute("data-variant-loading");
      this.container.removeAttribute("data-variant-dimmed");
    };

    fetch(`${productURL}?${params}`, { signal: controller.signal })
      .then(response => response.text())
      .then(text => {
        const newDoc = new DOMParser().parseFromString(text, "text/html");
        const newVariant = this._getCurrentVariant(newDoc);
        const newVariantId = newVariant?.id?.toString();

        if (!newVariantId) return;

        const idInput = qs(selectors.variantIdInput, this.formElement);
        if (idInput) idInput.value = newVariantId;

        // Keep [data-current-variant-json] in sync with the resolved variant
        const liveCurrentVariantEl = qs(
          selectors.currentVariantJson,
          this.container
        );
        const newCurrentVariantEl = qs(selectors.currentVariantJson, newDoc);
        if (liveCurrentVariantEl && newCurrentVariantEl) {
          liveCurrentVariantEl.textContent = newCurrentVariantEl.textContent;
        }

        this._swapInner(".product__block--product-header", newDoc);
        this._swapInner(".product__price-block", newDoc);
        this._swapInner(".product__vendor_and_sku", newDoc);
        updateInventoryCounterFromHTML(this.container, newDoc);
        this._replaceEl("[data-add-to-cart]", newDoc);

        // The variant-picker swap destroys the option <input> and swatch/chip
        // button nodes, so re-bind change / click listeners afterward.
        this._swapInner(".product__variants-wrapper", newDoc);
        this.productForm.refreshOptionInputs();
        this.productBlocks.refreshOptionButtons();

        this._swapInner(".sticky-atc-bar", newDoc);

        qsa("shopify-payment-terms", this.container).forEach(banner =>
          banner.setAttribute("variant-id", newVariantId)
        );

        const optionParentWrapper = srcElement.closest(".product__option");
        const optionLabel = optionParentWrapper?.querySelector(
          "[data-selected-value-for-option]"
        );
        if (optionLabel) optionLabel.textContent = srcElement.value;

        this.availability?.update(newVariant || { id: parseInt(newVariantId, 10) });

        cart.get().then(({ items }) => {
          cart.updateQuantitySelectorCounts(null, items, newVariant.id);
        });

        // Media gallery handling. Behavior matches the pre-SR-API flow:
        //   - Per-variant media grouping → full gallery reload from the SR
        //     response (heavyweight; only when the merchant opted in).
        //   - Otherwise switch / scroll to the variant's featured_media in
        //     the existing gallery. Branching matches the original code's
        //     three display modes: full product (thumbnails layout vs scroll
        //     layout) and quick-view.
        if (newVariant?.featured_media) {
          this.productMedia.pauseAllMedia();

          if (
            this.enableMultipleVariantMedia &&
            newVariant.featured_media.id != this.previousMediaId
          ) {
            this._swapMediaFromDoc(newDoc);
            this.previousMediaId = newVariant.featured_media.id;
          } else if (this.isFullProduct) {
            const mobileSlidesWrap = qs(selectors.photosMobile, this.container);
            const targetSlide = qs(
              `[data-media-item-id="${newVariant.featured_media.id}"]`,
              mobileSlidesWrap
            );

            if (targetSlide) {
              const targetSlideIndex = Array.from(
                targetSlide.parentElement.children
              ).indexOf(targetSlide);

              dispatchCustomEvent(
                `scroll-slider-${this.scrollerId}:go-to-slide`,
                { slideIndex: targetSlideIndex }
              );
            }

            const imagesWrap = qs(".product__media-container.above-mobile");

            if (imagesWrap?.dataset.desktopGalleryLayout === "thumbnails") {
              switchImage({
                container: this.photosDesktop,
                imageId: newVariant.featured_media.id,
                inYourSpaceButton: this.viewInYourSpace,
              });

              this.highlightActiveThumbnail(this.photosDesktop, newVariant);
              this.scrollThumbnails(newVariant);
            } else {
              const targetImage = qs(
                `.product__media-container.above-mobile [data-media-id="${newVariant.featured_media.id}"]`
              );

              if (
                this.isFeaturedProduct &&
                !window.matchMedia(getMediaQuery("below-960")).matches
              ) {
                this.switchCurrentImage(newVariant.featured_media.id);
              } else {
                targetImage?.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "nearest",
                });
              }
            }
          } else {
            this.switchCurrentImage(newVariant.featured_media.id);

            if (window.matchMedia(getMediaQuery("below-960")).matches) {
              this.photosQuickMobile &&
                this.highlightActiveThumbnail(
                  this.photosQuickMobile,
                  newVariant
                );
            } else {
              this.photosQuickDesktop &&
                this.highlightActiveThumbnail(
                  this.photosQuickDesktop,
                  newVariant
                );
            }

            this.scrollThumbnails(newVariant);
          }
        }

        if (!this.isQuickProduct && !this.isFeaturedProduct) {
          this.updateUrlWithVariant(newVariantId);
        }

        // Shopify Pay listens for `change` on the form to refresh dynamic checkout.
        this.formElement.dispatchEvent(new Event("change"));

        dispatchCustomEvent("product:variant-change", { variant: newVariant });
      })
      .catch(error => {
        // AbortError is expected when a newer option click supersedes this
        // request — not a real failure. Leave loading state for the new fetch.
        if (error.name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.error("Variant refresh failed:", error);
      })
      .finally(() => {
        // Skip cleanup when this request was aborted by a newer one — the
        // newer fetch owns the loading state now.
        if (controller.signal.aborted) return;
        cleanupLoading();
      });
  }

  _swapInner(selector, newDoc) {
    const newEls = qsa(selector, newDoc);
    const oldEls = qsa(selector, this.container);
    oldEls.forEach((oldEl, index) => {
      const newEl = newEls[index];
      if (newEl) oldEl.innerHTML = newEl.innerHTML;
    });
  }

  _replaceEl(selector, newDoc) {
    const newEl = qs(selector, newDoc);
    const oldEl = qs(selector, this.container);
    if (newEl && oldEl) {
      oldEl.replaceWith(newEl);
    }
  }

  // Replaces media gallery contents from a section response. Called only when
  // per-variant media grouping is enabled. Tears down + re-inits the media
  // subsystem afterward because the new container contains fresh DOM nodes.
  _swapMediaFromDoc(newDoc) {
    if (this.isQuickProduct) {
      const updatedDesktop = qs(".quick-product__left", newDoc);
      const updatedMobile = qs(
        ".product__block--product-header-image-wrap",
        newDoc
      );
      const quickDesktop = qs(".quick-product__left", this.container);
      const quickMobile = qs(
        ".product__block--product-header-image-wrap",
        this.container
      );

      if (updatedMobile && quickMobile) {
        quickMobile.innerHTML = updatedMobile.innerHTML;
      }
      if (updatedDesktop && quickDesktop) {
        quickDesktop.innerHTML = updatedDesktop.innerHTML;
      }
    } else {
      const updated = qs(".product__primary-left", newDoc);
      const target = qs(".product__primary-left", this.container);
      if (target && updated) {
        target.innerHTML = updated.innerHTML;
      }
    }

    this._unloadMedia();
    this.events.forEach(unsubscribe => unsubscribe());
    this._loadMedia();
  }

  updateUrlWithVariant(variantId) {
    const url = getUrlWithVariant(window.location.href, variantId);
    window.history.replaceState({ path: url }, "", url);
  }

  switchCurrentImage(id) {
    const imagesWraps = qsa(".product__media", this.container);
    imagesWraps.forEach(imagesWrap =>
      switchImage({
        container: imagesWrap,
        imageId: id,
      })
    );
  }

  highlightActiveThumbnail(photos, variant) {
    const thumb = qs(
      `[data-thumbnail-id="${variant.featured_media.id}"]`,
      photos
    );

    this.productThumbnailItems.forEach(thumb => remove(thumb, "active"));
    add(thumb, "active");
  }

  scrollThumbnails(variant) {
    if (!variant?.featured_media) return;

    this.productThumbnails.forEach((group, index) => {
      const groupThumb = qs(
        `[data-thumbnail-id="${variant.featured_media.id}"]`,
        group
      ).closest("li");

      this.productThumbnailsScrollers[index].scrollTo(groupThumb);
    });
  }

  // When user updates quantity
  onQuantityChange({ dataset: { quantity } }) {
    // Adjust the hidden quantity input within the form
    const quantityInputs = [...qsa('[name="quantity"]', this.formElement)];
    quantityInputs.forEach(quantityInput => {
      quantityInput.value = quantity;
    });

    dispatchCustomEvent("product:quantity-update", {
      quantity: quantity,
      variant: this._getCurrentVariant(),
    });
  }

  // When user submits the product form
  onFormSubmit(e) {
    const purchaseConfirmation = qs(selectors.purchaseConfirmation, document);
    const quickCart = qs(selectors.quickCart, document);
    const quickProductForm = e.target.closest(".quick-product");

    // If the quick cart or purchase confirmation aren't enabled, and we're not in a
    // quick view modal, just submit the form.
    //
    // If the quick cart or purchase confirmation aren't enabled, and we're in a
    // quick view modal without quick add enabled, just submit the form.
    //
    // Otherwise, add to cart with ajax and show the purchase confirmation or quick cart
    if (
      (!purchaseConfirmation && !quickCart && !quickProductForm) ||
      (!purchaseConfirmation &&
        !quickCart &&
        quickProductForm &&
        quickProductForm.dataset.quickAddEnabled === "false")
    )
      return;

    e.preventDefault();

    const buttonEls = qsa(selectors.addToCart, this.container);

    add(this.quantityError, "hidden");

    buttonEls.forEach(button => {
      add(button, "loading");
    });

    cart
      .addItem(this.formElement)
      .then(({ item }) => {
        buttonEls.forEach(button => {
          remove(button, "loading");
        });

        if (purchaseConfirmation) {
          emit("confirmation-popup:open", null, { product: item });
        } else {
          emit("quick-cart:open");
        }

        dispatchCustomEvent("cart:item-added", { product: item });
      })
      .catch(error => {
        cart.get(); // update local cart data

        if (error && error.message) {
          if (typeof error.message === "object") {
            const sectionID = qs(
              selectors.giftCardRecipientContainer,
              this.container
            ).dataset.sectionId;

            Object.entries(error.message).forEach(([key, value]) => {
              const errorMessageID = `display-gift-card-recipient-${key}-error--${sectionID}`;
              const errorMessage = qs(`#${errorMessageID}`, this.container);
              const errorInput = qs(
                `#display-gift-card-recipient-${key}--${sectionID}`,
                this.container
              );

              errorMessage.innerText = value;
              remove(errorMessage, "hidden");
              errorInput.setAttribute("aria-invalid", true);
              errorInput.setAttribute("aria-describedby", errorMessageID);
            });
          } else {
            this.quantityError.innerText = error.message;
            remove(this.quantityError, "hidden");
          }
        } else {
          this.quantityError.innerText = this.quantityError.getAttribute(
            "data-fallback-error-message"
          );
          remove(this.quantityError, "hidden");
        }

        const buttonEls = qsa(selectors.addToCart, this.container);

        remove(this.quantityError, "hidden");

        buttonEls.forEach(button => {
          remove(button, "loading");
        });
      });
  }

  _unloadMedia() {
    this.productMedia.pauseAllMedia();
    this.mobileSwiper?.destroy();
    this.productThumbnailsScrollers.forEach(scroller => scroller.unload());
  }

  unload() {
    this.variantFetchController?.abort();
    this.productForm.destroy();
    this.productBlocks.unload();
    this.events.forEach(unsubscribe => unsubscribe());
    this.stickyScroll?.destroy();
    this.sidebarRecommendations?.unload();
    this.productThumbnailsScrollers.forEach(scroller => scroller.unload());
    this.giftCardRecipient?.unload();
    this.stickyAtcBar?.unload();
  }
}

export default Product;
